'use client';

import React from 'react';
import type { ReactNode } from 'react';
import type { MenuItemProps as AriaMenuItemProps, MenuItemRenderProps } from 'react-aria-components';
import { MenuItem as AriaMenuItem } from 'react-aria-components';
import { cx } from '@milanote/roma/utils/cx';
import { CommandMenuItemBase as CommandMenuItemRootBase } from './command-menu-item-base';
import { styles } from './command-menu-item-styles';

const COMMAND_MENU_ITEM_DISPLAY_NAME = 'RomaCommandMenuItem';

export type CustomCommandMenuItemProps<T extends object> = Omit<
    AriaMenuItemProps<T>,
    'children' | 'className' | 'textValue'
> & {
    className?: AriaMenuItemProps<T>['className'];
    textValue: string;
    children: ReactNode | ((state: MenuItemRenderProps) => ReactNode);
};

/**
 * Renders a command menu item with fully custom row content while preserving shared item behavior.
 */
export const CustomCommandMenuItem = <T extends object>(props: CustomCommandMenuItemProps<T>) => {
    const { className, textValue, children, ...ariaProps } = props;

    return (
        <AriaMenuItem
            {...ariaProps}
            textValue={textValue}
            className={(state) =>
                cx(
                    COMMAND_MENU_ITEM_DISPLAY_NAME,
                    styles.common.menuItem,
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            {(state) => {
                const rootClassName = state.isDisabled ? styles.variants.disabled.root : styles.variants.default.root;

                return (
                    <CommandMenuItemRootBase
                        className={rootClassName}
                        isSelected={state.isSelected}
                        isDisabled={state.isDisabled}
                        isFocused={state.isFocused}
                        isFocusVisible={state.isFocusVisible}
                        isHovered={state.isHovered}
                    >
                        <div className={styles.common.row}>
                            {typeof children === 'function' ? children(state) : children}
                        </div>
                    </CommandMenuItemRootBase>
                );
            }}
        </AriaMenuItem>
    );
};

CustomCommandMenuItem.displayName = COMMAND_MENU_ITEM_DISPLAY_NAME;
